import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import AppleSignInButton from "@/components/AppleSignInButton";
import Logo from "@/components/Logo";
import LangToggle from "@/components/LangToggle";
import FreeTrialBadge from "@/components/FreeTrialBadge";
import PasswordStrength, { isPasswordStrong } from "@/components/PasswordStrength";
import PasswordInput from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useT } from "@/i18n";
import useSEO from "@/hooks/useSEO";

export default function Auth() {
  const { t } = useT();
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const refCode = (params.get("ref") || "").trim().toUpperCase().slice(0, 16);
  const [mode, setMode] = useState(refCode ? "register" : initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogleCredential, loginWithAppleWeb, user } = useAuth();
  const navigate = useNavigate();

  useSEO({
    title: mode === "register" ? "Create your Lumioos account — start your 14-day free trial" : "Sign in — Lumioos AI Workspace",
    description: mode === "register"
      ? "Sign up for Lumioos and unlock GPT-5, Claude, Sora 2 video, image gen and CV builder. 14-day free Creator trial, no credit card required."
      : "Sign in to Lumioos and pick up where you left off — AI chat, video producer, image generation, CV builder and more in one workspace.",
    canonical: "https://mylumioos.com/auth",
  });

  // Deep-link support — `next=/app/pair-desktop?...` sends the user
  // back to where they came from after login (used by the desktop
  // pairing flow so users land on the Pair page instead of /app/chat).
  const _decodeNext = (v) => {
    if (!v) return "/app";
    try {
      const decoded = decodeURIComponent(v);
      // Only allow same-origin destinations to prevent open-redirects.
      if (decoded.startsWith("/") && !decoded.startsWith("//")) return decoded;
    } catch { /* fall through */ }
    return "/app";
  };
  const nextParam = _decodeNext(params.get("next"));

  useEffect(() => {
    if (user) navigate(nextParam, { replace: true });
  }, [user, navigate, nextParam]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !isPasswordStrong(password)) {
      toast.error("Password is too weak. Please meet all 5 requirements below.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success(t("welcome_back"));
      } else {
        await register(name, email, password, refCode || undefined);
        toast.success(t("account_created"));
      }
      navigate(nextParam);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "weak_password") {
        toast.error(detail.message || "Password is too weak.");
      } else {
        const msg = detail || t("auth_failed");
        toast.error(typeof msg === "string" ? msg : t("auth_failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#FAFAFA]" data-testid="auth-page">
      <div className="flex flex-col px-6 sm:px-12 py-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="inline-flex"><Logo size="md" /></Link>
          <LangToggle variant="outline" />
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
              {mode === "login" ? t("auth_eyebrow_login") : t("auth_eyebrow_register")}
            </div>
            <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">
              {mode === "login" ? t("auth_h1_login") : t("auth_h1_register")}
            </h1>
            {mode === "register" && (
              <div className="mt-4">
                <FreeTrialBadge size="md" />
              </div>
            )}
            <p className="mt-3 text-zinc-600">
              {mode === "login" ? t("auth_sub_login") : t("auth_sub_register")}
            </p>

            <form onSubmit={submit} className="mt-10 space-y-4">
              <div className="w-full flex justify-center" data-testid="auth-google-btn">
                {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                <GoogleLogin
                  onSuccess={async (resp) => {
                    try {
                      await loginWithGoogleCredential(resp.credential);
                      toast.success(t("welcome_back"));
                      navigate(nextParam);
                    } catch (err) {
                      const msg = err?.response?.data?.detail || t("auth_failed");
                      toast.error(typeof msg === "string" ? msg : t("auth_failed"));
                    }
                  }}
                  onError={() => toast.error(t("auth_failed"))}
                  text={mode === "login" ? "signin_with" : "signup_with"}
                  shape="rectangular"
                  width="384"
                  locale={localStorage.getItem("smartai_lang") || "en"}
                />
              </div>

              <div className="w-full" data-testid="auth-apple-btn-wrap">
                <AppleSignInButton
                  label={mode === "login" ? "Sign in with Apple" : "Sign up with Apple"}
                  onSuccess={async ({ id_token, user: appleUser }) => {
                    try {
                      await loginWithAppleWeb({ id_token, user: appleUser });
                      toast.success(t("welcome_back"));
                      navigate(nextParam);
                    } catch (err) {
                      const msg = err?.response?.data?.detail || t("auth_failed");
                      toast.error(typeof msg === "string" ? msg : t("auth_failed"));
                    }
                  }}
                  onError={() => toast.error(t("auth_failed"))}
                />
              </div>

              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-zinc-200" />
                <span className="text-xs uppercase tracking-wider text-zinc-500">{t("auth_or")}</span>
                <div className="flex-1 h-px bg-zinc-200" />
              </div>

              {mode === "register" && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("auth_name")}</label>
                  <input
                    data-testid="auth-name-input"
                    required type="text" value={name} onChange={(e) => setName(e.target.value)}
                    className="mt-1 w-full px-4 py-3 border border-zinc-200 bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 rounded-md"
                  />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("auth_email")}</label>
                <input
                  data-testid="auth-email-input"
                  required type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full px-4 py-3 border border-zinc-200 bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 rounded-md"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("auth_password")}</label>
                <PasswordInput
                  data-testid="auth-password-input"
                  testidEye="auth-password-eye"
                  required minLength={mode === "register" ? 8 : 1}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full px-4 py-3 border border-zinc-200 bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 rounded-md"
                />
                {mode === "register" && <PasswordStrength password={password} />}
                {mode === "login" && (
                  <div className="mt-2 text-right">
                    <Link
                      to="/forgot-password"
                      data-testid="auth-forgot-link"
                      className="text-xs font-semibold text-zinc-600 hover:text-blue-600 hover:underline"
                    >
                      {t("auth_forgot")}
                    </Link>
                  </div>
                )}
              </div>

              <button
                type="submit" data-testid="auth-submit-btn" disabled={loading}
                className="btn-accent w-full inline-flex items-center justify-center gap-2 font-semibold py-3.5 rounded-md disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    {mode === "login" ? t("auth_signin") : t("auth_create")}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-sm text-zinc-600">
              {mode === "login" ? (
                <>
                  {t("auth_no_account")}{" "}
                  <button data-testid="auth-switch-register" type="button" onClick={() => setMode("register")} className="font-semibold text-blue-600 hover:underline">
                    {t("auth_create_one")}
                  </button>
                </>
              ) : (
                <>
                  {t("auth_have_account")}{" "}
                  <button data-testid="auth-switch-login" type="button" onClick={() => setMode("login")} className="font-semibold text-blue-600 hover:underline">
                    {t("auth_signin")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="text-center text-xs text-zinc-500 pb-4" data-testid="auth-legal-footer">
          <Link to="/privacy" className="hover:text-zinc-900 underline">Privacy</Link>
          <span className="mx-2">·</span>
          <Link to="/terms" className="hover:text-zinc-900 underline">Terms</Link>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/70 via-zinc-950/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">Lumioo</div>
          <blockquote className="mt-4 text-3xl font-bold tracking-tight text-white leading-tight max-w-md">
            &ldquo;I shipped 12 YouTube videos and resolved 4 freelance tickets last week — without lifting a finger on production.&rdquo;
          </blockquote>
          <div className="mt-6 text-white/80 text-sm">— Early beta user, NYC</div>
        </div>
      </div>
    </div>
  );
}
